import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, RiskBar, Severity, Skel, Stat } from '../components/ui'
import { thaiDateTime } from '../lib/format'

/**
 * AI audit master (part 70).
 *
 * The top of the page is the aggregated signal pressure across findings, incidents and alerts.
 * The queue below it holds ranked proposals. A proposal is advice: it cannot approve itself and
 * cannot execute before a human signs off and a dry run has been recorded. The API enforces that
 * order and the database repeats it; this page only makes the sequence visible.
 *
 * Execution records the approved decision and its deterministic outcome. This part never edits a
 * finding, an incident or an alert, so the page never claims that a source record changed.
 */

type ProposalStatus = 'pending' | 'approved' | 'rejected' | 'executed'
type RiskLevel = 'low' | 'medium' | 'high' | 'critical'
type Source = 'finding' | 'incident' | 'alert'

interface SignalRef {
  source: Source
  ref_id: string
  title: string
  severity: 'low' | 'medium' | 'high' | 'critical'
}

interface Proposal {
  id: string
  proposal_key: string
  title_th: string
  rationale_th: string
  action_kind: string
  status: ProposalStatus
  risk_score: number
  expected_impact: number
  risk_level: RiskLevel
  owner_role: string
  rollback_plan_th: string
  signal_count: number
  signal_digest: string
  evidence: SignalRef[]
  approved_by?: string
  approved_by_name?: string
  approved_by_kind?: string
  approved_by_role?: string
  approved_at?: string
  approval_note?: string
  rejected_by?: string
  rejected_by_name?: string
  rejected_at?: string
  rejection_note?: string
  dry_run_at?: string
  dry_run_action_id?: string
  executed_by?: string
  executed_by_name?: string
  executed_at?: string
  execution_note?: string
  approval_ready: boolean
  execution_ready: boolean
  created_at: string
  updated_at: string
  version: number
}

interface SourceSummary {
  source: Source
  total: number
  critical: number
  high: number
  medium: number
  low: number
  pressure: number
}

interface Snapshot {
  total: number
  critical: number
  high: number
  medium: number
  low: number
  pressure: number
  oldest_hours: number
  by_source: SourceSummary[]
  generated_at: string
}

interface SignalsResponse {
  snapshot: Snapshot
  signals: Array<{
    source: Source
    ref_id: string
    title: string
    severity: 'low' | 'medium' | 'high' | 'critical'
    risk_score: number
    status: string
    age_hours: number
  }>
}

interface Action {
  id: string
  proposal_id: string
  kind: 'dry_run' | 'execute'
  status: string
  actor_id?: string
  actor_name?: string
  actor_role?: string
  actor_kind: string
  approver_id?: string
  approver_name?: string
  approver_role?: string
  approved_at?: string
  result: Record<string, unknown>
  result_summary_th: string
  affected_signals: number
  writes_performed: number
  created_at: string
}

interface GenerateResponse {
  snapshot: Snapshot
  evaluated: number
  created: number
  proposals: Proposal[]
}

const PROPOSALS = ['ai-master-proposals'] as const
const SIGNALS = ['ai-master-signals'] as const
const proposalQuery = (id: string) => ['ai-master-proposal', id] as const
const historyQuery = (id: string) => ['ai-master-history', id] as const

const statusLabel: Record<ProposalStatus, string> = {
  pending: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ตีกลับ',
  executed: 'ดำเนินการแล้ว',
}

const statusTone: Record<ProposalStatus, string> = {
  pending: 'sev-medium',
  approved: 'sev-low',
  rejected: 'sev-critical',
  executed: 'dim',
}

const riskLabel: Record<RiskLevel, string> = {
  low: 'ต่ำ',
  medium: 'ปานกลาง',
  high: 'สูง',
  critical: 'วิกฤต',
}

const riskTone: Record<RiskLevel, string> = {
  low: 'sev-low',
  medium: 'sev-medium',
  high: 'sev-high',
  critical: 'sev-critical',
}

const sourceLabel: Record<Source, string> = {
  finding: 'ข้อค้นพบ',
  incident: 'เหตุการณ์',
  alert: 'การแจ้งเตือน',
}

const roleLabel: Record<string, string> = {
  owner: 'เจ้าขององค์กร',
  admin: 'ผู้ดูแลระบบ',
  audit_manager: 'ผู้จัดการตรวจสอบ',
  auditor: 'ผู้ตรวจสอบ',
  reviewer: 'ผู้ทบทวน',
  client_viewer: 'ผู้ชมฝั่งลูกค้า',
}

const actionLabel: Record<string, string> = {
  contain_findings: 'สกัดกั้นข้อค้นพบ',
  stabilise_incidents: 'ระงับเหตุการณ์',
  triage_alerts: 'จัดคิวการแจ้งเตือน',
  reassign_findings: 'มอบหมายข้อค้นพบใหม่',
  coordinate_response: 'ตอบสนองร่วมข้ามสัญญาณ',
  open_remediation: 'เปิดแผนแก้ไข',
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback
}

// Expected impact is a benefit, not a risk. RiskBar turns high values red, so the queue uses a
// separate emerald bar that reads correctly next to the risk score.
function ImpactBar({ score }: { score: number }) {
  return (
    <span className="row" style={{ gap: 10 }}>
      <span className="bar" style={{ width: 54 }}>
        <i style={{ width: `${Math.max(0, Math.min(100, score))}%`, background: 'var(--color-emerald)' }} />
      </span>
      <span className="tnum" style={{ minWidth: 22 }}>{score}</span>
    </span>
  )
}

export function AIMaster() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const canSignOff = ['owner', 'admin', 'audit_manager', 'reviewer'].includes(me?.role ?? '')
  const canWork = ['owner', 'admin', 'audit_manager', 'auditor', 'reviewer'].includes(me?.role ?? '')

  const signals = useQuery({
    queryKey: SIGNALS,
    queryFn: () => api<SignalsResponse>('/v1/ai/master/signals'),
  })
  const proposals = useQuery({
    queryKey: PROPOSALS,
    queryFn: () => api<Proposal[]>('/v1/ai/master/proposals'),
  })
  const detail = useQuery({
    queryKey: proposalQuery(selectedId ?? ''),
    queryFn: () => api<{ proposal: Proposal }>(`/v1/ai/master/proposals/${selectedId}`),
    enabled: Boolean(selectedId),
  })
  const history = useQuery({
    queryKey: historyQuery(selectedId ?? ''),
    queryFn: () => api<Action[]>(`/v1/ai/master/proposals/${selectedId}/history`),
    enabled: Boolean(selectedId),
  })

  const rows = proposals.data ?? []

  useEffect(() => {
    if (!selectedId && rows.length > 0) setSelectedId(rows[0].id)
  }, [rows, selectedId])

  const refresh = (id?: string) => {
    void qc.invalidateQueries({ queryKey: PROPOSALS })
    void qc.invalidateQueries({ queryKey: SIGNALS })
    if (id) {
      void qc.invalidateQueries({ queryKey: proposalQuery(id) })
      void qc.invalidateQueries({ queryKey: historyQuery(id) })
    }
  }

  const generate = useMutation({
    mutationFn: () => apiWrite<GenerateResponse>('/v1/ai/master/proposals/generate', 'POST', {}),
    onSuccess: (body) => {
      setNotice(
        body.created > 0
          ? `สร้างข้อเสนอใหม่ ${body.created} รายการ จากที่ประเมิน ${body.evaluated} รายการ`
          : 'ไม่มีข้อเสนอใหม่ เนื่องจากข้อเสนอเดิมยังเปิดอยู่หรือหลักฐานไม่เปลี่ยนแปลง',
      )
      void qc.invalidateQueries({ queryKey: PROPOSALS })
      void qc.invalidateQueries({ queryKey: SIGNALS })
      if (body.proposals[0]) setSelectedId(body.proposals[0].id)
    },
  })

  const approve = useMutation({
    mutationFn: (id: string) =>
      apiWrite<{ proposal: Proposal }>(`/v1/ai/master/proposals/${id}/approve`, 'POST', {
        note,
        actor_kind: 'human',
      }),
    onSuccess: (body) => {
      setNote('')
      setNotice(`อนุมัติข้อเสนอ ${body.proposal.proposal_key} แล้ว พร้อมดำเนินการหรือทดลองดำเนินการต่อ`)
      refresh(body.proposal.id)
    },
  })

  const reject = useMutation({
    mutationFn: (id: string) =>
      apiWrite<{ proposal: Proposal }>(`/v1/ai/master/proposals/${id}/reject`, 'POST', {
        note,
        actor_kind: 'human',
      }),
    onSuccess: (body) => {
      setNote('')
      setNotice(`ตีกลับข้อเสนอ ${body.proposal.proposal_key} แล้ว และไม่สามารถดำเนินการต่อได้`)
      refresh(body.proposal.id)
    },
  })

  const dryRun = useMutation({
    mutationFn: (id: string) =>
      apiWrite<{ proposal: Proposal; action: Action }>(`/v1/ai/master/proposals/${id}/dry-run`, 'POST', {
        actor_kind: 'human',
      }),
    onSuccess: (body) => {
      setNotice(`บันทึกผลทดลองดำเนินการแล้ว ${body.action.affected_signals} สัญญาณ โดยไม่มีการเขียนข้อมูลจริง`)
      refresh(body.proposal.id)
    },
  })

  const execute = useMutation({
    mutationFn: (id: string) =>
      apiWrite<{ proposal: Proposal; action: Action }>(`/v1/ai/master/proposals/${id}/execute`, 'POST', {
        note,
        actor_kind: 'human',
      }),
    onSuccess: (body) => {
      setNote('')
      setNotice(`ดำเนินการตามข้อเสนอ ${body.proposal.proposal_key} แล้ว ผู้อนุมัติคือ ${body.action.approver_name ?? '-'}`)
      refresh(body.proposal.id)
    },
  })

  const current = detail.data?.proposal
  const actions = history.data ?? []
  const mutationError = generate.error ?? approve.error ?? reject.error ?? dryRun.error ?? execute.error

  const counts = useMemo(() => {
    return {
      pending: rows.filter((r) => r.status === 'pending').length,
      approved: rows.filter((r) => r.status === 'approved').length,
      executed: rows.filter((r) => r.status === 'executed').length,
      rejected: rows.filter((r) => r.status === 'rejected').length,
    }
  }, [rows])

  return (
    <div className="page">
      <PageHead
        title="ศูนย์ควบคุม AI ด้านการตรวจสอบ"
        sub="รวมสัญญาณจากข้อค้นพบ เหตุการณ์ และการแจ้งเตือน แล้วจัดลำดับข้อเสนอโดยมีมนุษย์อนุมัติทุกครั้ง"
        actions={<span className="chip gold">ข้อเสนอโดยระบบ · อนุมัติโดยมนุษย์</span>}
      />

      {mutationError ? (
        <div className="form-error" style={{ marginBottom: 16 }}>
          {errorText(mutationError, 'ดำเนินการไม่สำเร็จ')}
        </div>
      ) : null}
      {notice ? (
        <div className="card card-pad rise" style={{ marginBottom: 16, borderColor: 'rgba(200,169,106,0.35)' }}>
          <div className="row" style={{ justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontSize: 12.5 }}>{notice}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => setNotice(null)}>ปิดข้อความ</button>
          </div>
        </div>
      ) : null}

      <div className="grid-4" style={{ marginBottom: 16 }}>
        <Stat
          label="แรงกดดันรวม"
          value={signals.data?.snapshot.pressure ?? 0}
          unit="/ 100"
          foot={`สัญญาณที่ยังเปิดอยู่ ${signals.data?.snapshot.total ?? 0} รายการ`}
        />
        <Stat label="รออนุมัติ" value={counts.pending} foot="ยังดำเนินการไม่ได้จนกว่ามนุษย์จะอนุมัติ" />
        <Stat label="อนุมัติแล้ว" value={counts.approved} foot="พร้อมดำเนินการหลังมนุษย์ลงนาม" />
        <Stat label="ดำเนินการแล้ว" value={counts.executed} foot="มีผู้อนุมัติและบันทึกผลการดำเนินการ" />
      </div>

      <div className="card card-pad" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
          <div style={{ maxWidth: 760 }}>
            <div className="card-label">ภาพรวมสัญญาณที่ยังเปิดอยู่</div>
            <p className="dim" style={{ fontSize: 12, lineHeight: 1.7, marginTop: 7 }}>
              ระบบอ่านข้อค้นพบ เหตุการณ์ และการแจ้งเตือนแบบอ่านอย่างเดียว แล้วคำนวณแรงกดดันและข้อเสนอแบบกำหนดแน่นอน
              ไม่มีการแก้ไขข้อค้นพบ เหตุการณ์ หรือการแจ้งเตือนจากหน้านี้ และไม่มีข้อใดดำเนินการเองได้
            </p>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
              {(signals.data?.snapshot.by_source ?? []).map((row) => (
                <span key={row.source} className="chip">
                  {sourceLabel[row.source]} {row.total} · วิกฤต {row.critical} · สูง {row.high} · แรงกดดัน {row.pressure}
                </span>
              ))}
              {signals.data ? (
                <span className="chip">เก่าที่สุด {signals.data.snapshot.oldest_hours} ชั่วโมง</span>
              ) : null}
            </div>
          </div>
          <button
            className="btn btn-primary"
            disabled={!canWork || generate.isPending}
            onClick={() => generate.mutate()}
          >
            {generate.isPending ? 'กำลังประเมิน…' : 'ประเมินและสร้างข้อเสนอ'}
          </button>
        </div>
        {!canWork ? <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>บทบาทของคุณสร้างข้อเสนอไม่ได้</div> : null}
      </div>

      <div className="grid-main">
        <div>
          <div className="row" style={{ marginBottom: 10 }}>
            <span className="card-label" style={{ margin: 0 }}>ข้อเสนอที่จัดลำดับแล้ว</span>
            <span className="chip" style={{ marginLeft: 'auto' }}>{rows.length} รายการ</span>
          </div>

          {proposals.isLoading ? (
            <Skel h={300} />
          ) : rows.length === 0 ? (
            <Empty
              title="ยังไม่มีข้อเสนอ"
              body="กดประเมินและสร้างข้อเสนอเพื่อให้ระบบรวมสัญญาณและจัดลำดับงานที่ควรทำก่อน"
            />
          ) : (
            <div className="table-wrap">
              <table className="table data">
                <thead>
                  <tr>
                    <th>ข้อเสนอ</th>
                    <th>ผลกระทบที่คาด</th>
                    <th>ความเสี่ยงของงาน</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      onClick={() => { setSelectedId(row.id); setNote('') }}
                      style={row.id === selectedId ? { background: 'rgba(200,169,106,0.055)' } : undefined}
                    >
                      <td style={{ maxWidth: 360 }}>
                        <div className="mono gold" style={{ fontSize: 11 }}>{row.proposal_key}</div>
                        <div style={{ lineHeight: 1.5, marginTop: 3 }}>{row.title_th}</div>
                        <div className="dim" style={{ fontSize: 10.5, marginTop: 3 }}>
                          {actionLabel[row.action_kind] ?? row.action_kind} · {row.signal_count} สัญญาณ
                        </div>
                      </td>
                      <td style={{ minWidth: 110 }}><ImpactBar score={row.expected_impact} /></td>
                      <td>
                        <span className={`sev ${riskTone[row.risk_level]}`} style={{ fontSize: 11 }}>
                          <i className="sev-dot" />
                          {riskLabel[row.risk_level]}
                        </span>
                        <div className="dim tnum" style={{ fontSize: 10.5, marginTop: 4 }}>คะแนนความเสี่ยง {row.risk_score}</div>
                      </td>
                      <td>
                        <span className={`sev ${statusTone[row.status]}`} style={{ fontSize: 11 }}>
                          {row.status !== 'executed' ? <i className="sev-dot" /> : null}
                          {statusLabel[row.status]}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="stack">
          {!selectedId ? (
            <div className="card card-pad dim" style={{ fontSize: 12 }}>เลือกข้อเสนอเพื่อดูหลักฐาน แผนย้อนกลับ และประวัติการอนุมัติ</div>
          ) : detail.isLoading ? (
            <Skel h={460} />
          ) : !current ? (
            <div className="card card-pad dim" style={{ fontSize: 12 }}>โหลดรายละเอียดข้อเสนอไม่สำเร็จ</div>
          ) : (
            <>
              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div>
                    <div className="mono gold" style={{ fontSize: 11 }}>{current.proposal_key}</div>
                    <h2 className="heading-m" style={{ marginTop: 7 }}>{current.title_th}</h2>
                    <div className="dim" style={{ fontSize: 11.5, marginTop: 5 }}>
                      {actionLabel[current.action_kind] ?? current.action_kind} · ผู้รับผิดชอบหลัก {roleLabel[current.owner_role] ?? current.owner_role}
                    </div>
                  </div>
                  <span className={`sev ${statusTone[current.status]}`}>
                    {current.status !== 'executed' ? <i className="sev-dot" /> : null}
                    {statusLabel[current.status]}
                  </span>
                </div>

                <p style={{ fontSize: 12.5, lineHeight: 1.75, marginTop: 14 }}>{current.rationale_th}</p>

                <div className="row" style={{ marginTop: 14, gap: 18, flexWrap: 'wrap' }}>
                  <div>
                    <div className="dim" style={{ fontSize: 10.5, marginBottom: 5 }}>แรงกดดันหากไม่ดำเนินการ</div>
                    <RiskBar score={current.risk_score} />
                  </div>
                  <div>
                    <div className="dim" style={{ fontSize: 10.5, marginBottom: 5 }}>ผลกระทบที่คาดว่าจะลดได้</div>
                    <ImpactBar score={current.expected_impact} />
                  </div>
                  <span className={`sev ${riskTone[current.risk_level]}`}>
                    <i className="sev-dot" />
                    ความเสี่ยงของงาน {riskLabel[current.risk_level]}
                  </span>
                </div>

                <div className="demo-note" style={{ marginTop: 14 }}>
                  ข้อเสนอนี้ไม่แก้ไขข้อมูลต้นทางด้วยตนเอง การอนุมัติเป็นการบันทึกเจตนาของมนุษย์
                  และการดำเนินการจะเกิดขึ้นได้หลังได้รับอนุมัติเท่านั้น การทดลองดำเนินการเป็นขั้นตอนเสริมเพื่อดูผลก่อนตัดสินใจ
                </div>
              </div>

              <div className="card card-pad">
                <div className="card-label">แผนย้อนกลับหากผลไม่เป็นไปตามคาด</div>
                <p style={{ fontSize: 12.5, lineHeight: 1.75, marginTop: 8 }}>{current.rollback_plan_th}</p>
              </div>

              <div className="card card-pad">
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
                  <span className="card-label" style={{ margin: 0 }}>หลักฐานประกอบข้อเสนอ</span>
                  <span className="chip">{current.signal_count} สัญญาณ</span>
                </div>
                {current.evidence.length === 0 ? (
                  <div className="dim" style={{ fontSize: 12 }}>ไม่มีหลักฐานบันทึกไว้</div>
                ) : (
                  <div className="stack" style={{ gap: 8 }}>
                    {current.evidence.map((ref) => (
                      <div key={`${ref.source}-${ref.ref_id}`} className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                        <span className="chip" style={{ flex: '0 0 auto' }}>{sourceLabel[ref.source]}</span>
                        <div style={{ fontSize: 12, lineHeight: 1.5 }}>
                          <div>{ref.title}</div>
                          <div className="dim mono" style={{ fontSize: 10 }}>{ref.ref_id}</div>
                        </div>
                        <span style={{ marginLeft: 'auto' }}><Severity value={ref.severity} /></span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="card card-pad">
                <div className="card-label">การตัดสินใจของมนุษย์</div>
                {current.approved_by ? (
                  <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.8 }}>
                    <div>อนุมัติโดย {current.approved_by_name} ({roleLabel[current.approved_by_role ?? ''] ?? current.approved_by_role})</div>
                    <div className="dim">{current.approved_at ? thaiDateTime(current.approved_at) : '-'}</div>
                    {current.approval_note ? <div className="dim">หมายเหตุ: {current.approval_note}</div> : null}
                  </div>
                ) : current.rejected_by ? (
                  <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.8 }}>
                    <div>ตีกลับโดย {current.rejected_by_name}</div>
                    <div className="dim">{current.rejected_at ? thaiDateTime(current.rejected_at) : '-'}</div>
                    {current.rejection_note ? <div className="dim">เหตุผล: {current.rejection_note}</div> : null}
                  </div>
                ) : (
                  <div className="dim" style={{ marginTop: 8, fontSize: 12 }}>ยังไม่มีการอนุมัติ ข้อเสนอนี้ดำเนินการไม่ได้</div>
                )}

                {current.dry_run_at ? (
                  <div style={{ marginTop: 12, fontSize: 12 }}>
                    ทดลองดำเนินการล่าสุด {thaiDateTime(current.dry_run_at)} · ไม่มีการเขียนข้อมูลจริง
                  </div>
                ) : (
                  <div className="dim" style={{ marginTop: 12, fontSize: 12 }}>ยังไม่มีผลทดลองดำเนินการ</div>
                )}

                {current.status === 'pending' || current.status === 'approved' ? (
                  <>
                    <div className="field">
                      <label htmlFor="ai-master-note">หมายเหตุประกอบการตัดสินใจ (ไม่บังคับ)</label>
                      <textarea
                        id="ai-master-note"
                        className="input"
                        rows={3}
                        maxLength={2000}
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="ระบุเหตุผลหรือเงื่อนไขที่ผู้อนุมัติควรทราบ"
                        style={{ width: '100%', height: 'auto', padding: '10px 12px' }}
                      />
                    </div>
                    <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                      {current.status === 'pending' ? (
                        <>
                          <button
                            className="btn btn-primary"
                            disabled={!canSignOff || approve.isPending}
                            onClick={() => approve.mutate(current.id)}
                          >
                            {approve.isPending ? 'กำลังอนุมัติ…' : 'อนุมัติข้อเสนอ'}
                          </button>
                          <button
                            className="btn btn-ghost"
                            disabled={!canSignOff || reject.isPending}
                            onClick={() => reject.mutate(current.id)}
                          >
                            {reject.isPending ? 'กำลังตีกลับ…' : 'ตีกลับ'}
                          </button>
                        </>
                      ) : null}
                      <button
                        className="btn btn-ghost"
                        disabled={!canWork || dryRun.isPending}
                        onClick={() => dryRun.mutate(current.id)}
                      >
                        {dryRun.isPending ? 'กำลังทดลอง…' : 'ทดลองดำเนินการ'}
                      </button>
                      {current.status === 'approved' ? (
                        <button
                          className="btn btn-primary"
                          disabled={!canSignOff || !current.execution_ready || execute.isPending}
                          onClick={() => execute.mutate(current.id)}
                        >
                          {execute.isPending ? 'กำลังดำเนินการ…' : 'ดำเนินการตามที่อนุมัติ'}
                        </button>
                      ) : null}
                    </div>
                    {current.status === 'approved' ? (
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                        แนะนำให้ทดลองดำเนินการก่อน เพื่อบันทึกผลและตรวจแผนย้อนกลับ แต่การอนุมัติเป็นเงื่อนไขเดียวของการดำเนินการ
                      </div>
                    ) : null}
                    {!canSignOff ? (
                      <div className="dim" style={{ fontSize: 11.5, marginTop: 8 }}>
                        การอนุมัติและดำเนินการต้องใช้บทบาท owner, admin, audit_manager หรือ reviewer
                      </div>
                    ) : null}
                  </>
                ) : (
                  <div className="dim" style={{ fontSize: 12, marginTop: 10 }}>ข้อเสนอนี้สิ้นสุดแล้ว ไม่มีการดำเนินการเพิ่มเติม</div>
                )}
              </div>

              <div className="card card-pad">
                <div className="card-label" style={{ marginBottom: 10 }}>ประวัติการทดลองและการดำเนินการ</div>
                {history.isLoading ? (
                  <Skel h={120} />
                ) : actions.length === 0 ? (
                  <div className="dim" style={{ fontSize: 12 }}>ยังไม่มีรายการในประวัติ</div>
                ) : (
                  <div className="table-wrap">
                    <table className="table data">
                      <thead>
                        <tr>
                          <th>เวลา</th>
                          <th>ประเภท</th>
                          <th>ผู้กระทำ</th>
                          <th>ผู้อนุมัติ</th>
                          <th>ผล</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actions.map((action) => (
                          <tr key={action.id}>
                            <td className="tnum" style={{ fontSize: 11 }}>{thaiDateTime(action.created_at)}</td>
                            <td style={{ fontSize: 11.5 }}>{action.kind === 'dry_run' ? 'ทดลอง' : 'ดำเนินการ'}</td>
                            <td style={{ fontSize: 11.5 }}>{action.actor_name || action.actor_id || '-'}</td>
                            <td style={{ fontSize: 11.5 }}>
                              {action.approver_name || '-'}
                              {action.approver_role ? <div className="dim" style={{ fontSize: 10 }}>{roleLabel[action.approver_role] ?? action.approver_role}</div> : null}
                            </td>
                            <td style={{ fontSize: 11.5 }}>
                              <div>{action.result_summary_th}</div>
                              <div className="dim tnum" style={{ fontSize: 10 }}>เขียนข้อมูล {action.writes_performed} รายการ · {action.affected_signals} สัญญาณ</div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
